import clsx from 'clsx';
import React, { ReactNode } from 'react';
import { grey } from '@material-ui/core/colors';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  ApiFilterOperation,
  Autocomplete,
  AutocompleteSource,
  FilterComponentProps,
  Select,
  useEventCallback,
} from '@eas/common-web';
import { Operation, useSelectOperations } from '@composite/filter/filter-api';
import { buildAutocompleteFilterState } from '@composite/filter/filter-cells/filter-autocomplete-cell';
import { useStyles as useFilterStyles } from '@composite/filter/filter-styles';
import { autocompleteLabelMapper } from '@components/form/misc/label-mappers';
import { DomainObject } from '@models';

const useItemStyles = makeStyles({
  autocompleteValue: {
    paddingLeft: 10,
    marginLeft: '-10px',
    borderBottom: `1px solid ${grey[300]}`,
    marginRight: '-10px',
    paddingRight: 10,
  },
});

function CustomAutocompleteItem<T>({
  item,
  children,
}: {
  item: T & { active?: boolean; deleted?: string };
  children: ReactNode;
}) {
  const classes = useItemStyles();
  const theme = useTheme();

  const inactiveOrDeleted = !item.active || item.deleted;

  const getInfoText = () => {
    if (!inactiveOrDeleted) {
      return '';
    }
    if (item.deleted) {
      return ` (role odebrána ${new Date(item.deleted).toLocaleDateString(
        'cs'
      )})`;
    }
    return ` (neaktivní)`;
  };

  return (
    <div
      className={classes.autocompleteValue}
      style={{
        backgroundColor: inactiveOrDeleted ? theme.palette.grey[100] : '#fff',
        color: inactiveOrDeleted ? theme.palette.grey[800] : '#000',
      }}
    >
      {children}
      {getInfoText()}
    </div>
  );
}

export function useCustomFilterAutocomplete<T extends DomainObject>({
  useSource,
}: {
  useSource: () => AutocompleteSource<T>;
}) {
  const FilterComponent = (props: FilterComponentProps) => {
    const classes = useFilterStyles();
    const operations = useSelectOperations();
    const operation =
      props.state.operation === ApiFilterOperation.NOT
        ? ApiFilterOperation.NOT
        : ApiFilterOperation.EQ;

    const handleChangeOperation = useEventCallback(
      (value: Operation | Operation[] | string | string[] | null) => {
        if (!value) {
          return;
        }

        props.onChangeState(
          buildAutocompleteFilterState(
            props.state,
            props.filter.filterkey,
            value as ApiFilterOperation,
            props.state.object ?? null,
            false
          )
        );
      }
    );

    const handleChange = useEventCallback((object: T | T[] | string | null) => {
      props.onChangeState(
        buildAutocompleteFilterState(
          props.state,
          props.filter.filterkey,
          operation,
          object,
          false
        )
      );
    });

    return (
      <div
        className={clsx(classes.root, {
          [classes.disabled]: props.disabled,
        })}
      >
        <div
          className={clsx(classes.selectWrapper, classes.compactSelectWrapper)}
        >
          <Select
            source={operations}
            value={operation}
            onChange={handleChangeOperation}
            valueIsId={true}
            clearable={false}
            disabled={props.disabled}
          />
        </div>
        <div className={classes.compactTextWrapper}>
          <Autocomplete<T>
            {...props}
            value={props.state.object}
            source={useSource()}
            labelMapper={autocompleteLabelMapper}
            ItemComponent={CustomAutocompleteItem}
            onChange={handleChange}
          />
        </div>
      </div>
    );
  };
  return FilterComponent;
}
